import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef } from 'react';
import {
  getAbsenceReferenceSelection,
  getReferenceWindowStart,
} from '../../lib';
import useTimeline from './timeline';

type AbsenceRouteSearch = {
  absenceIds?: string;
  date?: string;
  dateTo?: string;
};

/**
 * Opens the details panel for the days a task reference named.
 *
 * Task references land here with `?absenceIds=&date=&dateTo=`. Those days are
 * usually outside the month on screen, so the window is moved to hold the run
 * before anything is selected -- they are simply not in memory before then.
 *
 * The move is driven by the reference's own dates rather than by what has
 * resolved so far. A run reaching past the window resolves to the days inside
 * it and looks whole, so waiting for the selection to look short would never
 * fire: the reference has to say how far it reaches for "not loaded yet" and
 * "deleted since" to be told apart at all.
 */
export default function useAbsenceRouteFocus(): void {
  const {
    absences,
    calendar,
    focusAbsence,
    isError,
    isFetching,
    isPlaceholderData,
    visibleRange,
  } = useTimeline();
  const search = useSearch({ strict: false }) as AbsenceRouteSearch;
  const navigate = useNavigate();

  const absenceIds = useMemo(
    () =>
      (search.absenceIds ?? '')
        .split(',')
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0),
    [search.absenceIds],
  );
  const firstDate = search.date ?? null;
  // A reference carrying only one end names a single day.
  const lastDate = search.dateTo ?? search.date ?? null;

  const focusedRef = useRef<string | null>(null);

  const windowStart = useMemo(
    () =>
      firstDate && lastDate
        ? getReferenceWindowStart(firstDate, lastDate, visibleRange)
        : null,
    [firstDate, lastDate, visibleRange],
  );

  // Driven by the visible range rather than a one-shot flag, so a move that
  // lands before the calendar is mounted simply happens again on the next
  // render instead of leaving the target permanently out of reach.
  useEffect(() => {
    if (!absenceIds.length || !windowStart) {
      return;
    }
    calendar.setMonthViewFrom(windowStart.start, windowStart.days);
  }, [absenceIds, windowStart, calendar]);

  const focusKey = absenceIds.join(',');

  // Focusing takes the reference out of the URL, so the key of what was last
  // opened would otherwise stand for good -- and the sidebar makes clicking
  // the same task twice ordinary, not hypothetical. Forgetting it once the
  // reference is gone is what lets the second click land.
  useEffect(() => {
    if (!absenceIds.length) {
      focusedRef.current = null;
    }
  }, [absenceIds]);

  useEffect(() => {
    if (!absenceIds.length || focusedRef.current === focusKey) {
      return;
    }

    // The window is still on its way to the run, so what is in memory is not
    // yet all there is to have. Selecting now would show part of the run and,
    // worse, take the reference out of the URL -- leaving nothing to go back
    // for the rest with.
    if (windowStart) {
      return;
    }

    // The window has arrived but the days for it have not: the query holds the
    // last range's rows until the new ones land. Some of the run may well be
    // among them -- a reference running out of the month on screen overlaps
    // both -- and taking that for the whole of it is the same mistake as
    // selecting before the window moved.
    if (isPlaceholderData) {
      return;
    }

    const selection = getAbsenceReferenceSelection(absenceIds, absences);
    if (!selection?.start) {
      // Range still loading, or the days are gone; try again on next fetch.
      return;
    }

    // Short of what the reference names. Missing days mean deleted only once
    // this range has actually come back: while a request is in flight there
    // may be more, and if one failed the rows here are a cached copy that may
    // predate these days -- a change rewrites absences under new ids. Neither
    // "not placeholder" nor "no longer fetching" says the answer arrived.
    //
    // Holding leaves the reference in the URL, so a retry still lands. Opening
    // on part of a run is the one thing not to do: a withdrawal decision is
    // taken on the days the request named, not the days on screen.
    const answered = !isFetching && !isError;
    if (selection.absenceIds.length < absenceIds.length && !answered) {
      return;
    }

    focusedRef.current = focusKey;
    focusAbsence(selection, new Date(`${selection.start}T00:00:00`), (rows) =>
      getAbsenceReferenceSelection(absenceIds, rows),
    );
    navigate({ to: '/absence', search: {}, replace: true });
  }, [
    absenceIds,
    focusKey,
    windowStart,
    isError,
    isFetching,
    isPlaceholderData,
    absences,
    focusAbsence,
    navigate,
  ]);
}
